import { Scale } from "lucide-react";
import { useSeo } from "../lib/seo";
import { LegalPage } from "../components/legal/LegalPage";
import type { LegalSection } from "../components/legal/LegalPage";

const SECTIONS: LegalSection[] = [
  {
    id: "agreement",
    title: "The agreement",
    body: (
      <p>
        These Terms of Service ("Terms") are a binding agreement between the customer accepting
        them ("Customer", "you") and <strong>Shineywise Technologies OPC Pvt.&nbsp;Ltd.</strong> or{" "}
        <strong>Shineywise Technologies LLC</strong>, as identified on your order form or, absent
        one, the entity that invoices you (either, "Shineywise"). They govern access to and use of
        the Wise 2.0 platform (the "Platform"). By creating a workspace or using the Platform you
        accept these Terms on behalf of your organization and represent that you have authority to
        do so.
      </p>
    ),
  },
  {
    id: "definitions",
    title: "Definitions",
    body: (
      <ul className="space-y-1.5">
        <li><strong>Workspace</strong> — a tenant on the Platform, the unit of isolation, roles, and billing.</li>
        <li><strong>Connector</strong> — software (typically AI-generated) that connects the Platform to a Customer system, together with its capabilities and parameter schema.</li>
        <li><strong>Customer Data</strong> — data you submit to the Platform, including configurations, credentials, and content flowing through your integrations.</li>
        <li><strong>Exchange</strong> — the Connector Exchange, through which workspaces publish and install connectors.</li>
        <li><strong>Order Form</strong> — an ordering document or online purchase flow referencing these Terms.</li>
      </ul>
    ),
  },
  {
    id: "accounts",
    title: "Accounts & workspaces",
    body: (
      <>
        <p>
          You are responsible for the accuracy of registration information, the confidentiality of
          credentials, and all activity under your workspace. Roles (Owner, Admin, Builder,
          Operator, Viewer) control what members can do; assigning a role is your authorization for
          that member to act accordingly. Notify us promptly at{" "}
          <a className="font-medium text-brand-700 hover:underline" href="mailto:info@shineywise.com">
            info@shineywise.com
          </a>{" "}
          of any suspected unauthorized use.
        </p>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "Subscriptions, trials & fees",
    body: (
      <>
        <p>
          Paid plans are described on the applicable Order Form; fees are due as invoiced and,
          unless stated otherwise, are non-refundable and exclusive of taxes. Pilot, beta, and
          trial access is provided free of charge, "as available", and may be modified or
          withdrawn at any time.
        </p>
      </>
    ),
  },
  {
    id: "customer-data",
    title: "Customer Data & ownership",
    body: (
      <>
        <p>
          You retain all rights in Customer Data. You grant Shineywise a limited license to host
          and process Customer Data solely to provide the Platform, prevent abuse, and comply with
          law. We claim no ownership of your configurations or of connectors generated for your
          workspace. You may export your configurations (portable signed XML) and delete your
          workspace at any time.
        </p>
      </>
    ),
  },
  {
    id: "responsibilities",
    title: "Customer responsibilities",
    body: (
      <ul className="space-y-1.5">
        <li>You must have the right to connect each system you integrate and to use the credentials you provide.</li>
        <li>Your use of third-party systems through the Platform remains subject to those systems' own terms, rate limits, and API policies.</li>
        <li>You are responsible for reviewing generated integrations before relying on them for production-critical processes.</li>
        <li>You will not use the Platform to process data you are not lawfully permitted to process.</li>
      </ul>
    ),
  },
  {
    id: "ai-generated",
    title: "AI-generated integrations",
    body: (
      <>
        <p>
          Connectors and workflows are drafted by a large language model from your natural-language
          descriptions. Before registration, generated code is executed only in an isolated
          sandbox and must pass its generated test suite; configurations are cryptographically
          signed. Nevertheless, <strong>generated code is provided as a draft</strong>: Shineywise
          does not warrant that a generated connector is free of defects, fit for a particular
          purpose, or compliant with a third party's API terms. You are responsible for validating
          behavior against your systems before production use.
        </p>
      </>
    ),
  },
  {
    id: "exchange",
    title: "Connector Exchange",
    body: (
      <>
        <p>
          By publishing a connector, you grant Shineywise and every Platform workspace a
          non-exclusive, royalty-free license to host, reproduce, install, and run the published
          code bundle, capability list, and parameter schema. Configured values and secrets are
          never published. You represent that you have the rights necessary to publish the
          connector. Installing workspaces configure their own credentials and use published
          connectors at their own risk. Shineywise may remove listings that are unlawful, broken,
          or abusive. Unpublishing does not affect copies already installed.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <p>You will not, and will not permit anyone to:</p>
        <ul className="space-y-1.5">
          <li>attempt to escape the sandbox, probe, or circumvent Platform security or tenant isolation;</li>
          <li>use the Platform to build, distribute, or operate malware or to attack third-party systems;</li>
          <li>infringe intellectual-property, privacy, or export-control laws;</li>
          <li>resell or provide the Platform to third parties except as expressly agreed;</li>
          <li>interfere with the integrity or performance of the service or other workspaces.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ip",
    title: "Intellectual property",
    body: (
      <p>
        Shineywise retains all rights in the Platform, including its software, models of
        operation, documentation, and branding. Feedback you provide may be used to improve the
        Platform without obligation. Except for the licenses expressly granted here, no rights are
        transferred.
      </p>
    ),
  },
  {
    id: "confidentiality",
    title: "Confidentiality",
    body: (
      <p>
        Each party will protect the other's confidential information with at least reasonable
        care, use it only to perform under these Terms, and disclose it only to personnel and
        advisors bound by comparable obligations, or where required by law with prompt notice
        where lawful.
      </p>
    ),
  },
  {
    id: "availability",
    title: "Availability, support & maintenance",
    body: (
      <>
        <p>
          We operate continuous health monitoring: the monitoring agent re-tests connections,
          refreshes expiring credentials, and heals configuration drift, re-verifying every fix in
          the sandbox. Planned maintenance is announced in advance where practicable. During
          pilot/beta phases the Platform is provided without an SLA; enterprise SLAs are available
          under separate Order Forms. Support:{" "}
          <a className="font-medium text-brand-700 hover:underline" href="mailto:info@shineywise.com">
            info@shineywise.com
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "warranty",
    title: "Warranty disclaimer",
    body: (
      <p>
        EXCEPT AS EXPRESSLY STATED IN THESE TERMS, THE PLATFORM IS PROVIDED "AS IS" AND "AS
        AVAILABLE". SHINEYWISE DISCLAIMS ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, AND ANY
        WARRANTY THAT GENERATED INTEGRATIONS WILL BE ERROR-FREE OR UNINTERRUPTED.
      </p>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY IS LIABLE FOR INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, OR DATA; AND
        EACH PARTY'S AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS IS LIMITED TO
        THE FEES PAID OR PAYABLE BY CUSTOMER IN THE TWELVE MONTHS PRECEDING THE EVENT (OR USD 100
        FOR FREE-OF-CHARGE USE). THESE LIMITS DO NOT APPLY TO BREACHES OF CONFIDENTIALITY, IP
        INFRINGEMENT, OR AMOUNTS THAT CANNOT BE LIMITED BY LAW.
      </p>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnification",
    body: (
      <p>
        You will defend and indemnify Shineywise against third-party claims arising from Customer
        Data, your systems and credentials, or use of the Platform in violation of these Terms.
        Shineywise will defend and indemnify you against third-party claims that the Platform
        (excluding Customer Data and generated code you modified) infringes their intellectual
        property, and may, at its option, procure rights, modify the Platform, or terminate and
        refund prepaid unused fees.
      </p>
    ),
  },
  {
    id: "termination",
    title: "Suspension & termination",
    body: (
      <>
        <p>
          Either party may terminate for material breach not cured within 30 days of notice. We
          may suspend access immediately where necessary to protect the Platform or comply with
          law. Upon termination you may export Customer Data for 30 days, after which it is
          deleted per our retention policy. Sections that by nature survive (ownership,
          confidentiality, disclaimers, liability limits) survive termination.
        </p>
      </>
    ),
  },
  {
    id: "law",
    title: "Governing law & disputes",
    body: (
      <p>
        These Terms are governed by the laws applicable to the contracting Shineywise entity
        identified on your Order Form — for Shineywise Technologies OPC Pvt.&nbsp;Ltd., the laws
        of India; for Shineywise Technologies LLC, the laws of its state of organization — in each
        case excluding conflict-of-law rules. The parties will first attempt in good faith to
        resolve disputes informally within 30 days before initiating proceedings in the competent
        courts of the contracting entity's seat.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to these Terms",
    body: (
      <p>
        We may update these Terms; material changes are announced in-app and by email to workspace
        owners at least 14 days before taking effect. Continued use after the effective date
        constitutes acceptance. The "Last updated" date above reflects the current version.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Shineywise Technologies OPC Pvt.&nbsp;Ltd. · Shineywise Technologies LLC —{" "}
        <a className="font-medium text-brand-700 hover:underline" href="mailto:info@shineywise.com">
          info@shineywise.com
        </a>{" "}
        ·{" "}
        <a
          className="font-medium text-brand-700 hover:underline"
          href="https://shineywise.com"
          target="_blank"
          rel="noreferrer"
        >
          shineywise.com
        </a>
      </p>
    ),
  },
];

export default function Terms() {
  useSeo({
    title: 'Terms of Service',
    description: 'The terms governing use of the Wise 2.0 platform by Shineywise Technologies.',
    path: '/terms',
  });
  return (
    <LegalPage
      icon={Scale}
      eyebrow="Legal"
      title="Terms of Service"
      updated="July 2026"
      intro={
        <p>
          These Terms keep it simple: you own your data and your systems; we provide the platform
          that connects them. AI-generated integrations are sandbox-tested drafts you control,
          the Exchange shares code — never secrets — and enterprise protections
          (confidentiality, liability limits, indemnities) apply to both sides.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
